// AdminList — the ONE list surface every admin page uses (#358).
//
// Before this, the three admin lists were three different UIs: Organisationer
// put its search box outside the card and rendered rows; Användare put a filter
// inside the card and rendered rows; Kontaktförfrågningar rendered a six-column
// <Table> with its own header styling. Same job, three shapes, so moving
// between them meant re-learning where the controls live.
//
// One card, one header (title · count · search · filter · action), one row.
// A page supplies data and row content; it does not decide layout. Adding a new
// admin list means using this, not inventing a fourth arrangement.
//
// Rows rather than a table on purpose: these lists are scanned and clicked, not
// compared column-by-column, and rows survive narrow viewports without a
// horizontal scrollbar. Secondary facts go in the subtitle, state goes in
// `trailing`.
import React from 'react';
import { Search, AlertTriangle } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { NEXILINK_CARD_WHITE_ELEVATED, NEXILINK_ROW } from '@/lib/nexilink-card-styles';
import { cn } from '@/lib/utils';

interface AdminListProps {
    title: string;
    /** Rows after filtering. Rendered as "N av M" when `total` differs. */
    count: number;
    total?: number;
    search?: { value: string; onChange: (v: string) => void; placeholder?: string };
    /** A <Select> or similar. Sits beside the search box. */
    filter?: React.ReactNode;
    /** Primary action for the collection, e.g. "+ Ny organisation". */
    action?: React.ReactNode;
    isLoading?: boolean;
    /** Rendered instead of children when there is nothing to show. */
    empty?: React.ReactNode;
    /**
     * The load failed (#353). Takes precedence over `empty`, because the two are
     * NOT the same thing and showing the empty state for a failed request is how
     * "the backend is down" gets read as "there are no users". `onRetry` adds a
     * button — a dead list with no way to retry sends people to a page reload.
     */
    error?: string | null;
    onRetry?: () => void;
    children?: React.ReactNode;
}

export const AdminList: React.FC<AdminListProps> = ({
    title, count, total, search, filter, action, isLoading, empty, error, onRetry, children,
}) => {
    const showingSubset = typeof total === 'number' && total !== count;
    return (
        <div className={NEXILINK_CARD_WHITE_ELEVATED}>
            <div className="p-5 pb-3 flex flex-wrap items-center gap-3">
                <div className="min-w-0 mr-auto">
                    <h2 className="text-sm font-semibold text-gray-900">{title}</h2>
                    <p className="text-sm text-gray-500">
                        {showingSubset ? `${count} av ${total}` : count} {count === 1 ? 'post' : 'poster'}
                    </p>
                </div>
                {search && (
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <Input
                            value={search.value}
                            onChange={(e) => search.onChange(e.target.value)}
                            placeholder={search.placeholder ?? 'Sök…'}
                            className="pl-9 w-[240px]"
                        />
                    </div>
                )}
                {filter}
                {action}
            </div>

            {isLoading ? (
                <div className="px-6 pb-6 space-y-2">
                    {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-14 w-full" />)}
                </div>
            ) : error ? (
                // Before `empty`, deliberately — a failed load has count 0 too, and
                // whichever branch comes first is the one the user sees (#353).
                <div className="px-6 pb-6">
                    <div className="rounded-xl border border-red-200 bg-red-50 p-4">
                        <div className="flex items-start gap-2.5">
                            <AlertTriangle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                            <div className="min-w-0">
                                <p className="text-sm font-semibold text-red-900">
                                    Listan kunde inte hämtas
                                </p>
                                <p className="text-sm text-red-800 mt-0.5">
                                    Det här är ett fel — inte en tom lista. {error}
                                </p>
                                {onRetry && (
                                    <button
                                        type="button"
                                        onClick={onRetry}
                                        className="mt-2 text-sm font-medium text-red-700 hover:underline"
                                    >
                                        Försök igen
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            ) : count === 0 ? (
                <div className="px-6 pb-6">{empty}</div>
            ) : (
                <div className="px-2 pb-2 divide-y divide-gray-50">{children}</div>
            )}
        </div>
    );
};

interface AdminListRowProps {
    /** OrgLogo, avatar or IconChip — whatever identifies the thing. */
    leading?: React.ReactNode;
    title: React.ReactNode;
    subtitle?: React.ReactNode;
    /** Status, counts, per-row controls. Clicks here do NOT open the row. */
    trailing?: React.ReactNode;
    onClick?: () => void;
}

export const AdminListRow: React.FC<AdminListRowProps> = ({ leading, title, subtitle, trailing, onClick }) => (
    <div className={cn(NEXILINK_ROW, onClick && 'cursor-pointer')} onClick={onClick}>
        {leading}
        <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold text-gray-900 truncate">{title}</div>
            {subtitle && <div className="text-sm text-gray-500 truncate">{subtitle}</div>}
        </div>
        {trailing && (
            // Controls in the trailing slot (a status <Select>, a resend button)
            // must not also trigger the row's own navigation.
            <div className="flex items-center gap-2 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                {trailing}
            </div>
        )}
    </div>
);

export default AdminList;
